import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Trash2, Save, Recycle, Edit2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";

import { useHarvestComponents, useCompanyDisabledHarvestComponentIds } from "@/hooks/useHarvestComponents";
import { Switch } from "@/components/ui/switch";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import BrandHarvestSelector from "@/components/admin/BrandHarvestSelector";
import UnlinkedSparePartsPanel from "@/components/admin/UnlinkedSparePartsPanel";



interface NewComponent {
  name: string;
  description: string;
  default_weight_kg: string;
  default_value: string;
  default_recovery_pct: string;
  is_hazardous: boolean;
  appliance_category: string;
}

const emptyComp: NewComponent = {
  name: "", description: "",
  default_weight_kg: "", default_value: "", default_recovery_pct: "100", is_hazardous: false,
  appliance_category: "",
};

export default function HarvestingManagement({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const harvestTab = searchParams.get("tab") === "mapping" ? "mapping" : "components";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { allowed: isAdmin, isSuperadmin, isLoading } = useCanManageCompany();
  const { id: companyId } = useCurrentCompany();
  const { data: components = [] } = useHarvestComponents();
  const { data: disabledIds = new Set<string>() } = useCompanyDisabledHarvestComponentIds();

  const toggleSystemDefault = useMutation({
    mutationFn: async ({ componentId, enable }: { componentId: string; enable: boolean }) => {
      if (!companyId) throw new Error("No active company");
      if (enable) {
        const { error } = await supabase
          .from("company_disabled_harvest_components")
          .delete()
          .eq("company_id", companyId)
          .eq("component_id", componentId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("company_disabled_harvest_components")
          .insert({ company_id: companyId, component_id: componentId });
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["company_disabled_harvest_components"] });
      queryClient.invalidateQueries({ queryKey: ["harvest_components_for_appliance"] });
      queryClient.invalidateQueries({ queryKey: ["harvest_components_for_brand"] });
      toast({ title: vars.enable ? "Component enabled" : "Component disabled for your company" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<NewComponent>(emptyComp);
  const [showNew, setShowNew] = useState(false);
  const [newComp, setNewComp] = useState<NewComponent>(emptyComp);
  const [filterCategory, setFilterCategory] = useState<string>("__all__");

  const { categories: applianceCategories } = useCompanyApplianceCategories();

  const saveComponent = useMutation({
    mutationFn: async (data: NewComponent & { id?: string }) => {
      const payload = {
        name: data.name.trim(),
        description: data.description.trim() || null,
        category: null,
        material: null,
        default_weight_kg: parseFloat(data.default_weight_kg) || 0,
        default_value: parseFloat(data.default_value) || 0,
        default_recovery_pct: parseFloat(data.default_recovery_pct) || 100,
        is_hazardous: data.is_hazardous,
        appliance_category: data.appliance_category || null,
      } as any;
      if (data.id) {
        const { error } = await supabase.from("harvest_components").update(payload).eq("id", data.id);
        if (error) throw error;
      } else {
        if (!companyId) throw new Error("No active company");
        const { error } = await supabase.from("harvest_components").insert({
          ...payload,
          is_system_default: false,
          company_id: companyId,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harvest_components"] });
      setEditingId(null);
      setShowNew(false);
      setNewComp(emptyComp);
      toast({ title: "Component saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const deleteComponent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("harvest_components").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harvest_components"] });
      toast({ title: "Component deleted" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const startEdit = (c: any) => {
    setEditingId(c.id);
    setEditForm({
      name: c.name,
      description: c.description || "",
      default_weight_kg: String(c.default_weight_kg ?? ""),
      default_value: String(c.default_value ?? ""),
      default_recovery_pct: String(c.default_recovery_pct ?? "100"),
      is_hazardous: !!c.is_hazardous,
      appliance_category: c.appliance_category || "",
    });
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  // Access enforced by the route guard in App.tsx.

  const renderComponentForm = (form: NewComponent, setForm: (f: NewComponent) => void, onSave: () => void, onCancel: () => void) => (
    <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3">
      <div>
        <label className="text-xs font-medium text-foreground mb-1 block">Appliance Category *</label>
        <SearchableSelect
          value={form.appliance_category || null}
          onChange={(v) => setForm({ ...form, appliance_category: v ?? "" })}
          placeholder={applianceCategories.length ? "Choose an appliance category…" : "No categories enabled in Company Settings"}
          searchPlaceholder="Search categories…"
          emptyMessage="No categories found"
          items={applianceCategories.map((c) => ({ value: c.name, label: c.name }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="flex items-center gap-2">
          <Checkbox checked={form.is_hazardous} onCheckedChange={(c) => setForm({ ...form, is_hazardous: !!c })} />
          <span className="text-sm">Hazardous</span>
        </div>
        <Input placeholder="Weight (kg)" type="number" step="0.1" value={form.default_weight_kg} onChange={(e) => setForm({ ...form, default_weight_kg: e.target.value })} />
        <Input placeholder="Value (€)" type="number" step="0.01" value={form.default_value} onChange={(e) => setForm({ ...form, default_value: e.target.value })} />
        <Input placeholder="Recovery %" type="number" min="0" max="100" value={form.default_recovery_pct} onChange={(e) => setForm({ ...form, default_recovery_pct: e.target.value })} />
      </div>
      <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={!form.name.trim() || !form.appliance_category || saveComponent.isPending} className="gap-1">
          <Save className="w-3.5 h-3.5" /> Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );

  return (
    <div className={embedded ? "" : "min-h-screen bg-background pb-24"}>
      {!embedded && (
        <div className="px-6 pt-8 pb-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-primary hover:underline mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>{t("common.backToData")}</span>
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Recycle className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">{t("harvesting.title")}</h1>
          </div>
          <p className="text-muted-foreground">{t("harvesting.subtitle")}</p>
        </div>
      )}



      <div className="px-6">
        <Tabs value={harvestTab} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}>
          <TabsList>
            <TabsTrigger value="components">Components</TabsTrigger>
            <TabsTrigger value="mapping">Brand Mapping</TabsTrigger>
          </TabsList>






          <TabsContent value="components" className="space-y-3 mt-4">
            <div className="bg-card rounded-xl border border-border p-4">
              <label className="text-sm font-medium text-foreground mb-2 block">Filter by Appliance Category</label>
              <SearchableSelect
                className="max-w-sm"
                value={filterCategory}
                onChange={(v) => setFilterCategory(v ?? "__all__")}
                placeholder="Choose an appliance type…"
                searchPlaceholder="Search categories…"
                emptyMessage="No categories found"
                items={[
                  { value: "__all__", label: "All appliance categories" },
                  ...applianceCategories.map((c) => ({ value: c.name, label: c.name })),
                ]}
              />
            </div>

            {!showNew && (
              <Button variant="outline" className="gap-2 border-dashed w-full" onClick={() => { setShowNew(true); setNewComp({ ...emptyComp, appliance_category: filterCategory !== "__all__" ? filterCategory : "" }); }}>
                <Plus className="w-4 h-4" /> Add Component
              </Button>
            )}
            {showNew && renderComponentForm(newComp, setNewComp, () => saveComponent.mutate(newComp), () => setShowNew(false))}

            {(() => {
              const enabledNames = new Set(applianceCategories.map((c) => c.name));
              const scoped = components.filter((c) => c.appliance_category && enabledNames.has(c.appliance_category));
              const filtered = filterCategory !== "__all__"
                ? scoped.filter((c) => c.appliance_category === filterCategory)
                : scoped;
              const groups = new Map<string, typeof components>();
              filtered.forEach((c) => {
                const key = c.appliance_category || "Uncategorized";
                if (!groups.has(key)) groups.set(key, [] as any);
                (groups.get(key) as any).push(c);
              });
              const orderedKeys = Array.from(groups.keys()).sort();
              return orderedKeys.map((groupKey) => {
                const rows = (groups.get(groupKey) || []) as typeof components;
                return (
                  <div key={groupKey} className="space-y-2">
                    <div className="flex items-center gap-2 mt-2">
                      <h3 className="text-sm font-semibold text-foreground">{groupKey}</h3>
                      <span className="text-xs text-muted-foreground">({rows.length})</span>
                    </div>
                    {rows.map((c) => {
                      const isDisabled = c.is_system_default && disabledIds.has(c.id);
                      return (
                      <div key={c.id} className={`bg-card rounded-xl border border-border p-4 ${isDisabled ? "opacity-60" : ""}`}>
                        {editingId === c.id ? (
                          renderComponentForm(editForm, setEditForm, () => saveComponent.mutate({ ...editForm, id: c.id }), () => setEditingId(null))
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="font-medium text-foreground">{c.name}</span>
                                {c.is_system_default && (
                                  <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded">System default</span>
                                )}
                                {isDisabled && (
                                  <span className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded">Inactive for your company</span>
                                )}
                                {c.is_hazardous && <span className="px-2 py-0.5 text-xs bg-warning/20 text-warning rounded">Hazardous</span>}
                              </div>
                              {c.description && <p className="text-sm text-muted-foreground mb-1">{c.description}</p>}
                              <p className="text-xs text-muted-foreground">
                                {c.default_weight_kg}kg · €{c.default_value} · {c.default_recovery_pct}% recovery
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {c.is_system_default ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">{isDisabled ? "Off" : "On"}</span>
                                  <Switch
                                    checked={!isDisabled}
                                    disabled={toggleSystemDefault.isPending}
                                    onCheckedChange={(checked) =>
                                      toggleSystemDefault.mutate({ componentId: c.id, enable: !!checked })
                                    }
                                    aria-label={`Include ${c.name} for your company`}
                                  />
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => startEdit(c)}><Edit2 className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete ${c.name}?`)) deleteComponent.mutate(c.id); }} className="text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </TabsContent>

          <TabsContent value="mapping" className="mt-4 space-y-4">
            <BrandHarvestSelector mode="company" companyId={companyId ?? null} categories={applianceCategories.map((c) => c.name)} />
            <UnlinkedSparePartsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
